/**
 * Static controls tree definition for the permissions/access control system.
 *
 * This file defines the complete hierarchy of controls with explicit `isToggle`
 * property to distinguish between:
 * - Section headers (isToggle: false) - collapsible groupings
 * - Toggleable permissions (isToggle: true) - actual permissions that can be enabled/disabled
 */

import { type ControlCode, ControlCodes } from '@bcube/shared/constants';

export interface ControlNode {
  code: ControlCode;
  name: string;
  description: string;
  isToggle: boolean;
  children?: ControlNode[];
}

export const CONTROLS_TREE: ControlNode[] = [
  // ================================
  // ADMIN MODULE
  // ================================
  {
    code: ControlCodes.admin,
    name: 'Admin',
    description: 'Admin',
    isToggle: false,
    children: [
      {
        code: ControlCodes.admin_user,
        name: 'Usuários',
        description: 'Usuários',
        isToggle: true,
        children: [
          {
            code: ControlCodes.admin_user_view,
            name: 'Visualizar',
            description:
              'Permite acesso à tela de consulta de usuários do sistema, podendo aplicar filtros, fazer pesquisa e visualizar o grid com dados pesquisados.',
            isToggle: true,
          },
          {
            code: ControlCodes.admin_user_create,
            name: 'Novo Usuário',
            description:
              "Permite acesso ao botão 'Novo Usuário' e consequentemente à tela acessada através do botão em questão, podendo realizar cadastro de novos usuários do sistema.",
            isToggle: true,
          },
          {
            code: ControlCodes.admin_user_update,
            name: 'Editar',
            description:
              "Permite acesso ao botão 'Editar' da coluna 'Ações' do grid de resultados e consequentemente à tela acessada através do botão, onde poderá fazer edições dos dados do usuário, além de bloquear, fazer reset de senha, inativar e ativar.",
            isToggle: true,
          },
        ],
      },
      {
        code: ControlCodes.admin_profile,
        name: 'Perfis',
        description: 'Perfis',
        isToggle: true,
        children: [
          {
            code: ControlCodes.admin_profile_view,
            name: 'Visualizar',
            description:
              'Permite acesso à tela de consulta de perfis do sistema, podendo aplicar filtros, fazer pesquisa e visualizar o grid com dados pesquisados',
            isToggle: true,
          },
          {
            code: ControlCodes.admin_profile_create,
            name: 'Novo perfil',
            description:
              "Permite acesso ao botão 'Novo perfil' e consequentemente à tela acessada através do botão em questão, podendo realizar cadastro de novos perfis do sistema",
            isToggle: true,
          },
          {
            code: ControlCodes.admin_profile_inactive,
            name: 'Ativar/Inativar',
            description:
              "Permite acesso ao botão 'Ativar/Inativar' da coluna 'Ações' do grid de resultados, permitindo ao usuário ativar ou inativar perfis do sistema.",
            isToggle: true,
          },
        ],
      },
      {
        code: ControlCodes.admin_control,
        name: 'Controle de Acesso',
        description: 'Controle de Acesso',
        isToggle: true,
        children: [
          {
            code: ControlCodes.admin_control_view,
            name: 'Visualizar',
            description:
              'Permite acesso à tela de consulta de perfis do sistema, podendo aplicar filtros, fazer pesquisa e visualizar o grid com dados pesquisados',
            isToggle: true,
          },
          {
            code: ControlCodes.admin_control_update,
            name: 'Perfil',
            description:
              "Permite acesso ao campo 'Perfil', podendo o usuário aplicar um perfil específico e consequentemente adicionar ou remover acessos aos perfis do sistema.",
            isToggle: true,
          },
        ],
      },
    ],
  },

  // ================================
  // PRODUCTION MODULE
  // ================================
  {
    code: ControlCodes.production,
    name: 'Produção',
    description: 'Produção',
    isToggle: false,
    children: [
      {
        code: ControlCodes.production_device,
        name: 'Dispositivos',
        description: 'Dispositivos',
        isToggle: true,
      },
      {
        code: ControlCodes.production_configuration_profile,
        name: 'Perfil de configuração',
        description: 'Perfil de configuração',
        isToggle: true,
        children: [
          {
            code: ControlCodes.production_configuration_profile_view,
            name: 'Gestão',
            description: 'Gestão de Perfil de configuração',
            isToggle: true,
          },
        ],
      },
      {
        code: ControlCodes.production_tool,
        name: 'Ferramentas',
        description: 'Ferramentas',
        isToggle: true,
        children: [
          {
            code: ControlCodes.production_tool_configurator,
            name: 'Configurador',
            description: 'Configurador',
            isToggle: true,
          },
          {
            code: ControlCodes.production_tool_auto_test,
            name: 'Auto test',
            description: 'Auto test',
            isToggle: true,
          },
          {
            code: ControlCodes.production_tool_identifier,
            name: 'Identificador',
            description: 'Identificador',
            isToggle: true,
          },
        ],
      },
      {
        code: ControlCodes.production_logs,
        name: 'Logs',
        description: 'Logs',
        isToggle: true,
        children: [
          {
            code: ControlCodes.production_logs_configurator,
            name: 'Configurador',
            description: 'Logs do Configurador',
            isToggle: true,
          },
          {
            code: ControlCodes.production_logs_auto_test,
            name: 'Auto test',
            description: 'Logs do Auto test',
            isToggle: true,
          },
          {
            code: ControlCodes.production_logs_identifier,
            name: 'Identificador',
            description: 'Logs do Identificador',
            isToggle: true,
          },
        ],
      },
    ],
  },

  // ================================
  // COMMERCIAL MODULE
  // ================================
  {
    code: ControlCodes.commercial,
    name: 'Comercial',
    description: 'Comercial',
    isToggle: false,
    children: [
      {
        code: ControlCodes.commercial_client,
        name: 'Clientes',
        description: 'Clientes',
        isToggle: true,
      },
      {
        code: ControlCodes.commercial_proposal,
        name: 'Propostas',
        description: 'Propostas',
        isToggle: true,
      },
      {
        code: ControlCodes.commercial_accounts,
        name: 'Contas',
        description: 'Habilita módulo de contas',
        isToggle: true,
        children: [
          {
            code: ControlCodes.commercial_accounts_view,
            name: 'Visualizar',
            description:
              'Permite acesso à tela de consulta de contas (BCube > Home > Comercial > Conta), podendo aplicar filtros, fazer pesquisa e visualizar o grid com os dados pesquisados',
            isToggle: true,
            children: [
              {
                code: ControlCodes.commercial_accounts_view_lgpd_full,
                name: 'LGPD (total)',
                description:
                  "Permite a visualização de todos os dados de LGPD (CPF e RG) na aba 'Dados da conta' e na aba 'Histórico'.",
                isToggle: true,
              },
              {
                code: ControlCodes.commercial_accounts_view_lgpd_partial,
                name: 'LGPD (parcial)',
                description:
                  "Permite a visualização parcial dos dados de LGPD (CPF) na coluna 'CPF/CNPJ' do grid de resultados.",
                isToggle: true,
              },
            ],
          },
          {
            code: ControlCodes.commercial_accounts_access,
            name: 'Acessar Conta',
            description:
              "Permite acesso ao botão 'Acessar Conta' do grid de resultados da tela de contas (BCube > Home > Comercial > Conta > Botão 'Acessar Conta' do grid de resultados).",
            isToggle: true,
            children: [
              {
                code: ControlCodes.commercial_accounts_access_lgpd_full,
                name: 'LGPD (total)',
                description:
                  "Permite a visualização todos dos dados de LGPD (CPF e RG) na aba 'Dados da conta' e na aba 'Histórico'.",
                isToggle: true,
              },
              {
                code: ControlCodes.commercial_accounts_access_lgpd_partial,
                name: 'LGPD (parcial)',
                description:
                  "Permite a visualização parcial dos dados de LGPD (CPF e RG) na aba 'Dados da conta' e na aba 'Histórico'.",
                isToggle: true,
              },
              {
                code: ControlCodes.commercial_accounts_access_tab_data,
                name: 'Aba Dados da Conta',
                description:
                  "Permite acesso a aba 'Dados da Conta' (BCube > Home > Comercial > Conta > Botão 'Acessar Conta' do grid de resultados > Aba 'Dados da Conta).",
                isToggle: true,
                children: [
                  {
                    code: ControlCodes.commercial_accounts_access_tab_data_edit,
                    name: 'Editar/Atualizar Dados',
                    description:
                      "Permite acesso aos botões 'Atualizar' e 'Editar' dentro do bloco de 'Dados'",
                    isToggle: true,
                  },
                  {
                    code: ControlCodes.commercial_accounts_access_tab_data_group_edit,
                    name: 'Editar Grupo Econômico',
                    description:
                      "Permite acesso ao botão 'Editar' dentro do bloco 'Grupo Econômico'",
                    isToggle: true,
                  },
                  {
                    code: ControlCodes.commercial_accounts_access_tab_data_contacts,
                    name: 'Cadastrar/Editar/Excluir Contatos',
                    description:
                      "Permite acesso aos botões 'Buscar contato', 'Novo', 'Editar' e 'Excluir' do bloco 'Contatos'",
                    isToggle: true,
                  },
                  {
                    code: ControlCodes.commercial_accounts_access_tab_data_addresses,
                    name: 'Cadastrar/Editar/Excluir Endereços',
                    description:
                      "Permite acesso aos botões 'Novo', 'Editar', 'Excluir' e 'Copiar' do bloco 'Endereços'",
                    isToggle: true,
                  },
                ],
              },
              {
                code: ControlCodes.commercial_accounts_access_tab_history,
                name: 'Aba Histórico',
                description:
                  "Permite acesso a aba 'Histórico' (BCube > Home > Comercial > Conta > Botão 'Acessar Conta' do grid de resultados > Aba 'Histórico').",
                isToggle: true,
              },
              {
                code: ControlCodes.commercial_accounts_access_tab_attachments,
                name: 'Aba Anexos',
                description:
                  "Permite acesso a aba 'Anexos' (BCube > Home > Comercial > Conta > Botão 'Acessar Conta' do grid de resultados > Aba 'Anexos').",
                isToggle: true,
                children: [
                  {
                    code: ControlCodes.commercial_accounts_access_tab_attachments_delete,
                    name: 'Excluir Anexos',
                    description: "Permite acesso ao botão 'Excluir' do grid de anexos",
                    isToggle: true,
                  },
                ],
              },
            ],
          },
          {
            code: ControlCodes.commercial_accounts_new,
            name: 'Nova Conta',
            description:
              "Permite acesso ao botão 'Nova Conta' da tela de contas (BCube > Home > Comercial > Conta > Botão 'Nova Conta')",
            isToggle: true,
          },
          {
            code: ControlCodes.commercial_accounts_new_sector,
            name: 'Novo tipo de setor',
            description:
              'Permite acesso ao botão "Novo tipo de setor", podendo o usuário cadastrar novos setores e inativar setores já cadastrados.',
            isToggle: true,
          },
        ],
      },
      {
        code: ControlCodes.commercial_price_table,
        name: 'Tabela de Preços',
        description: 'Modulo tabela de preços (BCube > Home > Comercial > Tabela de Preços).',
        isToggle: true,
        children: [
          {
            code: ControlCodes.commercial_price_table_view,
            name: 'Tabela de Preços - Visualizar',
            description:
              'Permite acesso à tela de consulta da tabela de preços (BCube > Home > Comercial > Tabela de Preços), podendo aplicar filtros, fazer pesquisa e visualizar o grid com os dados pesquisados.',
            isToggle: true,
          },
          {
            code: ControlCodes.commercial_price_table_create,
            name: 'Tabela de Preços - Nova Tabela',
            description:
              "Permite acesso ao botão para cadastro de novas tabelas de preços (BCube > Home > Comercial > Tabela de Preços > botão 'Nova Tabela').",
            isToggle: true,
          },
          {
            code: ControlCodes.commercial_price_table_edit,
            name: 'Tabela de Preços - Editar',
            description:
              "Permite acesso ao botão editar do grid de resultados da tabela de preços, podendo o usuário visualizar detalhes das tabelas e fazer alterações nas mesmas (BCube > Home > Comercial > Tabela de Preços > Grid de resultados > Coluna 'Ação' > botão 'Editar').",
            isToggle: true,
          },
        ],
      },
      {
        code: ControlCodes.commercial_credit_analysis,
        name: 'Análise de Crédito',
        description: 'Módulo de análise de crédito de clientes.',
        isToggle: true,
      },
      {
        code: ControlCodes.commercial_product,
        name: 'Produtos',
        description:
          'Módulo principal de Produtos dentro do Comercial, agrupando funcionalidades de gestão, categorias, transações, estoque e análises.',
        isToggle: true,
        children: [
          {
            code: ControlCodes.commercial_product_view,
            name: 'Gestão',
            description:
              'Permite acesso à tela de gestão de produtos (BCube > Home > Comercial > Produtos), onde é possível visualizar, pesquisar e administrar os cadastros de produtos.',
            isToggle: true,
          },
          {
            code: ControlCodes.commercial_product_category,
            name: 'Categorias',
            description:
              'Permite acesso às categorias de produtos (BCube > Home > Comercial > Produtos > Categorias), possibilitando a organização, criação e manutenção das categorias.',
            isToggle: true,
          },
          {
            code: ControlCodes.commercial_product_category_create,
            name: 'Criar Categoria de Produtos',
            description:
              'Permite acesso ao botão para cadastro de novas categorias de produtos (BCube > Home > Comercial > Produtos > Categorias > Nova Categoria).',
            isToggle: true,
          },
          {
            code: ControlCodes.commercial_product_transaction,
            name: 'Entradas e Saídas',
            description:
              'Permite acesso às movimentações de produtos (entradas e saídas) no módulo de Comercial (BCube > Home > Comercial > Produtos > Entradas e Saídas), para controle de fluxo e histórico.',
            isToggle: true,
          },
          {
            code: ControlCodes.commercial_product_stock,
            name: 'Estoque',
            description:
              'Permite acesso ao controle de estoque de produtos (BCube > Home > Comercial > Produtos > Estoque), exibindo quantidades disponíveis, movimentações e status atual.',
            isToggle: true,
          },
          {
            code: ControlCodes.commercial_product_analysis,
            name: 'Análise',
            description:
              'Permite acesso à área de análise de produtos (BCube > Home > Comercial > Produtos > Análise), com indicadores, relatórios e métricas de desempenho.',
            isToggle: true,
          },
          {
            code: ControlCodes.commercial_product_create,
            name: 'Criar Produto',
            description:
              'Permite acesso ao botão para cadastro de novos produtos (BCube > Home > Comercial > Produtos > Novo Produto).',
            isToggle: true,
          },
        ],
      },
    ],
  },

  // ================================
  // CONNECTIVITY MODULE
  // ================================
  {
    code: ControlCodes.connectivity,
    name: 'Conectividade',
    description: 'Conectividade',
    isToggle: false,
    children: [
      {
        code: ControlCodes.connectivity_kora_sync,
        name: 'Sincronização KORA',
        description: 'Sincronização KORA',
        isToggle: true,
      },
      {
        code: ControlCodes.connectivity_lora_activation,
        name: 'Ativação LoRaWan',
        description: 'Ativação LoRaWan',
        isToggle: true,
      },
    ],
  },
];
